import React, { useEffect, useState, useRef, forwardRef } from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import Button from 'scripts/components/Button'
import app from 'scripts/init/react/Bootstrapper'
import { trackGtmEvent } from 'scripts/util/tracking'
import codeSplit from 'scripts/higher-order-components/codeSplit'

const ModalBlock = forwardRef(
  (
    {
      anchor,
      buttonText,
      children,
      className,
      headerColor,
      hideButton,
      isEditor,
      isOpen: isOpenProp,
      // eslint-disable-next-line no-unused-vars
      l10n,
      ModalComponent = codeSplit({
        loadComponent: () =>
          /* global System */
          System.import(
            /* webpackChunkName: "Modal" */
            'scripts/components/Modal'
          ),
      }),
      modalRoot,
      onRequestClose,
      // eslint-disable-next-line no-unused-vars
      proxy,
      title,
      ...rest
    },
    forwardedRef
  ) => {
    const [isOpen, updateOpen] = useState(false)
    const buttonRef = useRef()
    const ref = useRef()
    const isControlled = typeof isOpenProp === 'boolean'

    const closeModal = () => {
      if (!isEditor) {
        history.replaceState(null, null, ' ')
      }
      updateOpen(false)
    }

    const openModal = linkText => {
      if (!isEditor) {
        trackGtmEvent('modal', {
          'link-text': linkText ? linkText : buttonText,
        })
      }
      updateOpen(true)
    }

    const finalIsOpen = isControlled ? isOpenProp : isOpen

    const openModalHash = `#${anchor}`

    const openByClick = e => {
      if (isEditor) return

      const {
        target: { hash, innerText, pathname },
      } = e

      if (hash === openModalHash && pathname === window.location.pathname) {
        openModal(innerText)
      }
    }

    useEffect(() => {
      window.addEventListener('click', openByClick)
      return () => window.removeEventListener('click', openByClick)
    }, [])

    useEffect(() => {
      const {
        location: { hash },
      } = window

      if (hash === openModalHash) openModal()
    }, [])

    return (
      <div
        {...rest}
        ref={forwardedRef}
        className={classnames('dhsv-modal-block', className)}
      >
        {isEditor || !hideButton ? (
          <Button
            ref={buttonRef}
            url={openModalHash}
            aria-haspopup="dialog"
            aria-expanded={finalIsOpen}
          >
            {buttonText}
          </Button>
        ) : null}
        <ModalComponent
          className={'content'}
          isOpen={finalIsOpen}
          modalRoot={modalRoot}
          onAfterOpen={() => app.update(ref.current)}
          onRequestClose={isControlled ? onRequestClose : closeModal}
          style={headerColor}
          title={title}
        >
          {typeof children !== 'string' ? (
            <div ref={ref}>{children}</div>
          ) : (
            <div ref={ref} dangerouslySetInnerHTML={{ __html: children }} />
          )}
        </ModalComponent>
      </div>
    )
  }
)

ModalBlock.displayName = 'ModalBlock'

ModalBlock.propTypes = {
  anchor: PropTypes.string.isRequired,
  buttonText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  className: PropTypes.string,
  headerColor: PropTypes.string,
  hideButton: PropTypes.bool,
  isEditor: PropTypes.bool,
  isOpen: PropTypes.bool,
  l10n: PropTypes.object,
  ModalComponent: PropTypes.elementType,
  modalRoot: PropTypes.instanceOf(Element),
  onRequestClose: PropTypes.func,
  proxy: PropTypes.instanceOf(Element),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
}

export default ModalBlock
